import React, { useEffect, useState } from "react";
import { Link, NavLink, useNavigate, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import RadioMenu from "@/components/RadioMenu";
import {
  Trees, LogOut, Menu, X,
  UserCircle2, MessageSquare, PenSquare, Search,
  Users, Tent, FileText, Mountain, MailOpen, Newspaper, CreditCard, Building2,
  BookOpen, Map, LifeBuoy, Home, Globe2, Shield, Library,
} from "lucide-react";
import RightRail from "@/components/RightRail";
import { READY_GUIDES } from "@/lib/guides";

const FULL_WIDTH_PREFIXES = ["/messages", "/travel"];

/* Sidebar groups — top-level items + their children (sub-actions/searches). */
const groups = [
  {
    key: "home",
    label: "Home Page",
    icon: Home,
    to: "/home",
    children: [],
  },
  {
    key: "profile",
    label: "Profile",
    icon: UserCircle2,
    to: "/profile",
    children: [
      { label: "My blog", to: "/blog", icon: BookOpen },
    ],
  },
  {
    key: "travel",
    label: "Travel Planner",
    icon: Map,
    to: "/travel",
    children: [],
  },
  {
    key: "forum",
    label: "Discussion Forum",
    icon: MessageSquare,
    to: "/forum",
    children: [
      { label: "Write post", to: "/forum/new", icon: PenSquare },
      { label: "Search posts", to: "/forum?focus=search", icon: Search },
    ],
  },
  {
    key: "guides",
    label: "Nomad Guides",
    icon: Library,
    to: "/guides",
    children: READY_GUIDES.map((g) => ({ label: g.title, to: `/guides/${g.slug}`, icon: BookOpen })),
  },
  {
    key: "members",
    label: "Members",
    icon: Users,
    to: "/members",
    children: [
      { label: "Search", to: "/members?focus=search", icon: Search },
    ],
  },
  {
    key: "operators",
    label: "Operators & Communities",
    icon: Building2,
    to: "/operators",
    children: [],
  },
  {
    key: "ads",
    label: "Community Ads",
    icon: Tent,
    to: "/community-ads",
    children: [
      { label: "Personal ad · €1", to: "/community-ads/new?type=seeking", icon: FileText, price: "€1" },
      { label: "Vehicle ad · €1", to: "/community-ads/new?type=vehicle", icon: FileText, price: "€1" },
      { label: "Digital Nomad Housing · €1", to: "/community-ads/new?type=digital_nomad_housing", icon: FileText, price: "€1" },
      { label: "Property / Land ad · €10", to: "/community-ads/new?type=construction_sale", icon: Mountain, price: "€10" },
    ],
  },
  {
    key: "services",
    label: "Global Services",
    icon: Globe2,
    to: "/global-services",
    children: [],
  },
  {
    key: "messages",
    label: "Messages",
    icon: MailOpen,
    to: "/messages",
    children: [],
  },
  {
    key: "newsletter",
    label: "Newsletter",
    icon: Newspaper,
    to: "/newsletter",
    children: [
      { label: "This Month's Newsletter", to: "/newsletter#this-month", icon: Newspaper },
    ],
  },
  {
    key: "billing",
    label: "Membership",
    icon: CreditCard,
    to: "/billing",
    children: [],
  },
  {
    key: "support",
    label: "Contact Support",
    icon: LifeBuoy,
    to: "/support",
    children: [],
  },
];

export default function MemberLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);

  // Poll for unread DM count every 45s. This is the *only* in-app
  // notification the platform will ever show — a small emerald dot next to
  // the Messages sidebar entry. No push notifications, no toasts, no badges
  // anywhere else. See privacy policy section 7.
  useEffect(() => {
    if (!user || user.role === "admin") return;
    let active = true;
    const tick = async () => {
      try {
        const { data } = await api.get("/messages/unread-count");
        if (active) setUnread(Number(data?.unread) || 0);
      } catch {
        /* silent — notification dot must never break the app */
      }
    };
    tick();
    const id = setInterval(tick, 45000);
    // Also refresh when the user returns to the tab so the dot clears
    // promptly after they've read a conversation elsewhere.
    const onFocus = () => tick();
    window.addEventListener("focus", onFocus);
    return () => { active = false; clearInterval(id); window.removeEventListener("focus", onFocus); };
  }, [user, location.pathname]);

  if (user === undefined) {
    return <div className="min-h-screen flex items-center justify-center text-[var(--hh-sage)] label-eyebrow">Loading…</div>;
  }
  if (!user) {
    nav("/login", { replace: true });
    return null;
  }

  const onLogout = async () => {
    await logout();
    nav("/");
  };

  const isActive = (to) => {
    const base = to.split("?")[0];
    return location.pathname === base || location.pathname.startsWith(base + "/");
  };

  const showRail = !FULL_WIDTH_PREFIXES.some((p) => location.pathname.startsWith(p));

  return (
    <div className="min-h-screen" style={{ background: "var(--hh-bg)" }}>
      {/* Top header — visible on all viewports, contains the Home Page anchor (centered) */}
      <header className="glass-header sticky top-0 z-50" data-testid="member-top-header">
        <div className="relative px-5 md:px-8 h-14 flex items-center justify-between gap-4">
          {/* Left spacer (mobile menu toggle lives here on small screens) */}
          <div className="flex-1 flex justify-start">
            <button onClick={() => setOpen(v => !v)} className="md:hidden text-[var(--hh-cream)]" data-testid="member-menu-toggle">
              {open ? <X /> : <Menu />}
            </button>
          </div>

          {/* Centered Home Page button */}
          <div className="absolute left-1/2 -translate-x-1/2">
            <NavLink
              to="/home"
              className={({ isActive: a }) =>
                `flex items-center gap-2 px-3 py-1.5 border ${a ? "border-[var(--hh-accent)] text-[var(--hh-accent)]" : "border-[var(--hh-border)] text-[var(--hh-cream)] hover:border-[var(--hh-sage)]"}`
              }
              data-testid="header-home-btn"
            >
              <Trees size={16} color="var(--hh-accent)" />
              <span className="text-sm tracking-wide">Home Page</span>
            </NavLink>
          </div>

          {/* Right: email on desktop */}
          <div className="flex-1 flex justify-end">
            <span className="text-xs label-eyebrow hidden md:inline">{user.email}</span>
          </div>
        </div>
      </header>

      <div className={`md:grid md:grid-cols-[280px_1fr] ${showRail ? "xl:grid-cols-[280px_1fr_300px]" : ""}`} data-testid="member-holy-grail">
        {/* Sidebar */}
        <aside
          className={`${open ? "block" : "hidden"} md:block border-r border-[var(--hh-border)] md:min-h-screen md:sticky md:top-14 md:max-h-[calc(100vh-3.5rem)] md:overflow-y-auto scroll-area`}
          style={{ background: "rgba(20, 36, 29, 0.7)" }}
          data-testid="member-sidebar"
        >
          <div className="border-b border-[var(--hh-border)] px-6 py-3">
            <RadioMenu />
          </div>
          <div className="flex items-center gap-2 px-6 h-16 border-b border-[var(--hh-border)]">
            <Trees size={20} color="var(--hh-accent)" />
            <Link to="/home" className="font-display text-2xl">Nomad <span className="text-[var(--hh-accent)]">Life</span></Link>
          </div>
          {user?.role === "admin" && (
            <div className="border-b border-[var(--hh-border)] px-4 py-3">
              <Link
                to="/admin"
                onClick={() => setOpen(false)}
                className="flex items-center gap-2 text-xs label-eyebrow text-[var(--hh-accent)]"
                data-testid="sidenav-admin-panel"
              >
                <Shield size={12}/> Open admin panel
              </Link>
            </div>
          )}

          <nav className="px-4 py-6 space-y-1">
            {groups.map(g => {
              const Icon = g.icon;
              const active = isActive(g.to);
              return (
                <div key={g.key}>
                  <NavLink
                    to={g.to}
                    onClick={() => setOpen(false)}
                    className={`flex items-center gap-3 px-3 py-2.5 border ${active ? "border-[var(--hh-accent)] text-[var(--hh-accent)]" : "border-transparent text-[var(--hh-cream)] hover:border-[var(--hh-sage)]"}`}
                    data-testid={`sidenav-${g.key}`}
                  >
                    <Icon size={16} />
                    <span className="text-sm tracking-wide">{g.label}</span>
                    {g.key === "messages" && unread > 0 && (
                      <span
                        aria-label={`${unread} unread messages`}
                        title={`${unread} unread`}
                        className="ml-auto inline-block w-2 h-2 rounded-full"
                        style={{ background: "#10b981", boxShadow: "0 0 0 2px rgba(16,185,129,0.25)" }}
                        data-testid="sidenav-messages-dot"
                      />
                    )}
                  </NavLink>
                  {g.children.length > 0 && active && (
                    <div className="ml-4 mt-1 mb-3 border-l border-[var(--hh-border)] pl-3 space-y-1">
                      {g.children.map(c => {
                        const Ic = c.icon;
                        return (
                          <NavLink
                            key={c.to}
                            to={c.to}
                            onClick={() => setOpen(false)}
                            className={({ isActive: a }) =>
                              `flex items-center gap-2 px-2 py-2 text-xs ${a ? "text-[var(--hh-accent)]" : "text-[var(--hh-sage)] hover:text-[var(--hh-cream)]"}`
                            }
                            data-testid={`sidenav-${g.key}-${c.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                          >
                            <Ic size={13} />
                            <span>{c.label}</span>
                          </NavLink>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}

            {/* Sign out — placed directly under Contact Support as a sibling nav item */}
            <button
              type="button"
              onClick={onLogout}
              className="w-full text-left flex items-center gap-3 px-3 py-2.5 border border-transparent text-[var(--hh-cream)] hover:border-[var(--hh-sage)]"
              data-testid="sidenav-logout"
            >
              <LogOut size={16} />
              <span className="text-sm tracking-wide">Sign out</span>
            </button>
          </nav>

          <div className="px-4 py-6 border-t border-[var(--hh-border)] mt-4">
            <p className="text-xs label-eyebrow truncate" data-testid="sidenav-email">{user.email}</p>
          </div>
        </aside>

        {/* Main content */}
        <div className="min-w-0 xl:contents">
          <main className="min-h-screen min-w-0">
            <Outlet />
          </main>
          {showRail && <RightRail />}
        </div>
      </div>

      <footer className="border-t border-[var(--hh-border)] px-6 md:px-8 py-6 flex flex-col sm:flex-row items-center justify-between gap-3" data-testid="member-footer">
        <p className="text-xs label-eyebrow">© {new Date().getFullYear()} Nomad Life</p>
        <nav className="flex gap-5 text-xs label-eyebrow">
          <Link to="/guides" className="link-hh" data-testid="member-footer-guides">Guides</Link>
          <Link to="/terms" className="link-hh" data-testid="member-footer-terms">Terms</Link>
          <Link to="/privacy" className="link-hh" data-testid="member-footer-privacy">Privacy</Link>
          <Link to="/cookies" className="link-hh" data-testid="member-footer-cookies">Cookies</Link>
        </nav>
      </footer>
    </div>
  );
}
